import { useEffect, useState } from 'react';
import { withWindow } from '@shared/utils/withWindow';

/** How long without input before the user counts as idle. */
export const USER_IDLE_TIMEOUT_MS = 30_000;

const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'] as const;

type IdleListener = (idle: boolean) => void;

/**
 * One tracker for the whole page, however many components subscribe. Input events only
 * write a timestamp (they fire at frame rate, so they must stay cheap), and a single timer
 * re-arms itself only when it fires, instead of every event clearing and re-creating it.
 */
const listeners = new Set<IdleListener>();
let isIdle = false;
let lastActivityAt = 0;
let idleTimer: ReturnType<typeof setTimeout> | undefined;

const setIdle = (next: boolean) => {
  if (isIdle === next) return;
  isIdle = next;
  listeners.forEach(listener => listener(next));
};

const checkIdle = () => {
  const remaining = USER_IDLE_TIMEOUT_MS - (Date.now() - lastActivityAt);
  if (remaining > 0) {
    idleTimer = setTimeout(checkIdle, remaining);
    return;
  }
  // No timer while idle: the next activity re-arms it.
  idleTimer = undefined;
  setIdle(true);
};

const armIdleTimer = () => {
  if (idleTimer === undefined) idleTimer = setTimeout(checkIdle, USER_IDLE_TIMEOUT_MS);
};

const handleActivity = () => {
  lastActivityAt = Date.now();
  setIdle(false);
  armIdleTimer();
};

// A hidden tab counts as idle at once: nobody is watching, and browsers don't reliably stop playback.
const handleVisibilityChange = (event: Event) => {
  if ((event.currentTarget as Document).visibilityState === 'hidden') {
    clearTimeout(idleTimer);
    idleTimer = undefined;
    setIdle(true);
  } else {
    handleActivity();
  }
};

const start = (win: Window) => {
  ACTIVITY_EVENTS.forEach(event => win.addEventListener(event, handleActivity, { passive: true, capture: true }));
  win.document.addEventListener('visibilitychange', handleVisibilityChange);
  // Mounting counts as activity: the page was just opened or navigated to.
  handleActivity();
};

const stop = (win: Window) => {
  ACTIVITY_EVENTS.forEach(event => win.removeEventListener(event, handleActivity, { capture: true }));
  win.document.removeEventListener('visibilitychange', handleVisibilityChange);
  clearTimeout(idleTimer);
  idleTimer = undefined;
  isIdle = false;
};

/**
 * Whether the user has gone {@link USER_IDLE_TIMEOUT_MS} without pointer, touch, key, wheel or
 * scroll input, or the tab is hidden. Flips back to `false` on the next input.
 *
 * Pass `enabled: false` to skip tracking; the hook then always returns `false`.
 * Always `false` during SSR and the first client render, so markup hydrates identically.
 */
export const useIsUserIdle = (enabled = true): boolean => {
  const [idle, setIdleState] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIdleState(false);
      return undefined;
    }
    return withWindow<(() => void) | undefined>(win => {
      if (listeners.size === 0) start(win);
      listeners.add(setIdleState);
      setIdleState(isIdle);
      return () => {
        listeners.delete(setIdleState);
        if (listeners.size === 0) stop(win);
      };
    }, undefined);
  }, [enabled]);

  return idle;
};
